import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useApi } from '../../hooks/useApi';
import { getProgrammingLesson, updateProgrammingLesson, generateProgrammingTasks } from '../../services/api';
import './ProgrammingLessonEditor.css';
import ErrorCard from '../../components/ErrorCard/ErrorCard';

const ProgrammingLessonEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { handleRequest } = useApi();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [lesson, setLesson] = useState(null);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generateForm, setGenerateForm] = useState({
    taskCount: '1'
  });
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    language: 'html',
    level: 'basic',
    lessonLanguage: 'en',
    tasks: []
  });

  useEffect(() => {
    if (!user || (user.role !== 'teacher' && user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/dashboard');
      return;
    }

    const loadLesson = async () => {
      try {
        setLoading(true);
        const response = await handleRequest(getProgrammingLesson, id);
        const lessonData = response.data;
        
        setLesson(lessonData);
        setFormData({
          title: lessonData.title || '',
          description: lessonData.description || '',
          language: lessonData.language || 'html',
          level: lessonData.level || 'basic',
          lessonLanguage: lessonData.lessonLanguage || 'en',
          tasks: lessonData.tasks || []
        });
        setErrorMessage('');
      } catch (error) {
        setErrorMessage(error.message || 'Kunne ikke laste inn programmeringsleksjonen');
      } finally {
        setLoading(false);
      }
    };

    loadLesson();
  }, [id, user, navigate, handleRequest]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleTaskChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map((task, i) => 
        i === index ? { ...task, [field]: value } : task
      )
    }));
  };

  const handleStarterCodeChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      tasks: prev.tasks.map((task, i) => {
        if (i === index) {
          return { ...task, [field]: value };
        }
        return task;
      })
    }));
  };

  const addTask = () => {
    const newTask = {
      taskNumber: formData.tasks.length + 1,
      continuance: false,
      description: '',
      instructions: '',
      starterCode: '',
      starterCodeHTML: '',
      starterCodeCSS: '',
      starterCodeJS: '',
      expectedOutput: '',
      testCases: [],
      hints: [],
      resources: ''
    };

    setFormData(prev => ({
      ...prev,
      tasks: [...prev.tasks, newTask]
    }));
  };

  const removeTask = (index) => {
    if (window.confirm('Er du sikker på at du vil fjerne denne oppgaven?')) {
      setFormData(prev => ({
        ...prev,
        tasks: prev.tasks.filter((_, i) => i !== index).map((task, i) => ({
          ...task,
          taskNumber: i + 1
        }))
      }));
    }
  };

  const handleGenerateTasks = async () => {
    try {
      setGenerating(true);
      setErrorMessage('');

      if (!formData.description || !formData.description.trim()) {
        setErrorMessage('Leksjonsbeskrivelse er påkrevd for å generere oppgaver');
        setGenerating(false);
        return;
      }

      const taskCount = parseInt(generateForm.taskCount);
      if (!taskCount || taskCount < 1 || taskCount > 10) {
        setErrorMessage('Antall oppgaver må være mellom 1 og 10');
        setGenerating(false);
        return;
      }

      const response = await handleRequest(
        generateProgrammingTasks, 
        id, 
        { taskCount }
      );

      const generatedTasks = response.data;
      
      // Add generated tasks to existing tasks
      setFormData(prev => ({
        ...prev,
        tasks: [...prev.tasks, ...generatedTasks.map((task, idx) => ({
          ...task,
          taskNumber: prev.tasks.length + idx + 1
        }))]
      }));

      setShowGenerateModal(false);
      setGenerateForm({ taskCount: '1' });
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke generere oppgaver');
    } finally {
      setGenerating(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setErrorMessage('');

      if (!formData.title.trim()) {
        setErrorMessage('Tittel er påkrevd');
        setSaving(false);
        return;
      }

      if (formData.tasks.length === 0) {
        setErrorMessage('Legg til minst én oppgave');
        setSaving(false);
        return;
      }

      // Validate all tasks
      for (let i = 0; i < formData.tasks.length; i++) {
        const task = formData.tasks[i];
        if (!task.description || !task.description.trim()) {
          setErrorMessage(`Oppgave ${i + 1}: Beskrivelse er påkrevd`);
          setSaving(false);
          return;
        }
        if (!task.instructions || !task.instructions.trim()) {
          setErrorMessage(`Oppgave ${i + 1}: Instruksjoner er påkrevd`);
          setSaving(false);
          return;
        }
        if (!task.expectedOutput || !task.expectedOutput.trim()) {
          setErrorMessage(`Oppgave ${i + 1}: Forventet resultat er påkrevd`);
          setSaving(false);
          return;
        }
      }

      const formattedData = {
        title: formData.title.trim(),
        description: formData.description.trim(),
        language: formData.language,
        tasks: formData.tasks.map((task, index) => ({
          taskNumber: index + 1,
          continuance: !!task.continuance,
          description: task.description.trim(),
          instructions: task.instructions.trim(),
          starterCode: task.starterCode || '',
          starterCodeHTML: task.starterCodeHTML || '',
          starterCodeCSS: task.starterCodeCSS || '',
          starterCodeJS: task.starterCodeJS || '',
          expectedOutput: task.expectedOutput.trim(),
          testCases: task.testCases || [],
          hints: task.hints || [],
          resources: task.resources || ''
        }))
      };

      await handleRequest(updateProgrammingLesson, id, formattedData);
      navigate('/dashboard');
    } catch (error) {
      setErrorMessage(error.message || 'Kunne ikke oppdatere programmeringsleksjonen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="programming-lesson-editor-container">
        <div className="loading">Laster programmeringsleksjon...</div>
      </div>
    );
  }

  const isMixed = formData.language === 'mixed';

  return (
    <div className="programming-lesson-editor-container">
      <div className="programming-lesson-editor-header">
        <h1>Rediger programmeringsleksjon</h1>
        <button 
          className="back-btn"
          onClick={() => navigate('/dashboard')}
        >
          ← Tilbake til oversikten
        </button>
      </div>

      {errorMessage && <ErrorCard message={errorMessage} />}

      <form onSubmit={handleSubmit} className="programming-lesson-editor-form">
        <div className="form-section">
          <h2>Leksjonsinformasjon</h2>
          
          <div className="form-group">
            <label htmlFor="title">Leksjonstittel</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Skriv inn leksjonstittel"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Leksjonsbeskrivelse</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Skriv inn leksjonsbeskrivelse (brukes til å generere oppgaver)"
              rows="4"
              required
            />
          </div>

          <div className="form-row">
            <div className="form-group half-width">
              <label htmlFor="language">Programmeringsspråk</label>
              <select
                id="language"
                name="language"
                value={formData.language}
                onChange={handleChange}
                required
              >
                <option value="html">HTML</option>
                <option value="css">CSS</option>
                <option value="javascript">JavaScript</option>
                <option value="mixed">Blandet (HTML/CSS/JS)</option>
              </select>
            </div>

            <div className="form-group half-width">
              <label htmlFor="level">Vanskelighetsgrad</label>
              <select
                id="level"
                name="level"
                value={formData.level}
                onChange={handleChange}
                required
              >
                <option value="basic">Grunnleggende</option>
                <option value="intermediate">Middels</option>
                <option value="advanced">Avansert</option>
              </select>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="section-header">
            <h2>Oppgaver ({formData.tasks.length})</h2>
            <button 
              type="button"
              className="generate-tasks-btn"
              onClick={() => setShowGenerateModal(true)}
            >
              Generer oppgaver
            </button>
          </div>

          {formData.tasks.map((task, tIndex) => (
            <div key={tIndex} className="task-card">
              <div className="task-header">
                <h3>Oppgave {tIndex + 1}</h3>
                <button
                  type="button"
                  className="remove-task-btn"
                  onClick={() => removeTask(tIndex)}
                  title="Fjern oppgave"
                >
                  ×
                </button>
              </div>

              <div className="form-group">
                <label>Beskrivelse</label>
                <input
                  type="text"
                  value={task.description}
                  onChange={(e) => handleTaskChange(tIndex, 'description', e.target.value)}
                  placeholder="Kort oppgavebeskrivelse"
                  required
                />
              </div>

              <div className="form-group">
                <label>Instruksjoner</label>
                <textarea
                  value={task.instructions}
                  onChange={(e) => handleTaskChange(tIndex, 'instructions', e.target.value)}
                  placeholder="Detaljerte steg-for-steg-instruksjoner"
                  rows="3"
                  required
                />
              </div>

              {isMixed ? (
                <>
                  <div className="form-group">
                    <label>Startkode (HTML)</label>
                    <textarea
                      value={task.starterCodeHTML || ''}
                      onChange={(e) => handleStarterCodeChange(tIndex, 'starterCodeHTML', e.target.value)}
                      placeholder="HTML-startkode"
                      rows="4"
                    />
                  </div>
                  <div className="form-group">
                    <label>Startkode (CSS)</label>
                    <textarea
                      value={task.starterCodeCSS || ''}
                      onChange={(e) => handleStarterCodeChange(tIndex, 'starterCodeCSS', e.target.value)}
                      placeholder="CSS-startkode"
                      rows="4"
                    />
                  </div>
                  <div className="form-group">
                    <label>Startkode (JavaScript)</label>
                    <textarea
                      value={task.starterCodeJS || ''}
                      onChange={(e) => handleStarterCodeChange(tIndex, 'starterCodeJS', e.target.value)}
                      placeholder="JavaScript-startkode"
                      rows="4"
                    />
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label>Startkode</label>
                  <textarea
                    value={task.starterCode || ''}
                    onChange={(e) => handleTaskChange(tIndex, 'starterCode', e.target.value)}
                    placeholder="Startkode for å hjelpe elevene i gang"
                    rows="4"
                  />
                </div>
              )}

              <div className="form-group">
                <label>Forventet resultat</label>
                <textarea
                  value={task.expectedOutput}
                  onChange={(e) => handleTaskChange(tIndex, 'expectedOutput', e.target.value)}
                  placeholder="Tydelig beskrivelse av hva koden skal produsere"
                  rows="2"
                  required
                />
              </div>

              <div className="form-group">
                <label>Ressurser og nyttig informasjon (valgfritt)</label>
                <textarea
                  value={task.resources || ''}
                  onChange={(e) => handleTaskChange(tIndex, 'resources', e.target.value)}
                  placeholder="Nyttig informasjon, begreper, syntakseksempler eller referanser"
                  rows="3"
                />
              </div>
            </div>
          ))}

          {formData.tasks.length === 0 && (
            <div className="no-tasks">
              <p>Ingen oppgaver ennå. Trykk «Legg til oppgave» eller «Generer oppgaver» for å komme i gang.</p>
            </div>
          )}

          <div className="task-actions-footer">
            <button 
              type="button"
              className="add-task-btn"
              onClick={addTask}
            >
              + Legg til oppgave
            </button>
          </div>
        </div>

        {showGenerateModal && (
          <div className="modal-overlay" onClick={() => setShowGenerateModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Generer oppgaver</h3>
                <button 
                  className="modal-close-btn"
                  onClick={() => setShowGenerateModal(false)}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="generate-count">Antall oppgaver</label>
                  <input
                    type="number"
                    id="generate-count"
                    min="1"
                    max="10"
                    value={generateForm.taskCount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 10)) {
                        setGenerateForm(prev => ({
                          ...prev,
                          taskCount: value
                        }));
                      }
                    }}
                    onBlur={(e) => {
                      const value = parseInt(e.target.value);
                      if (!value || value < 1) {
                        setGenerateForm(prev => ({ ...prev, taskCount: '1' }));
                      } else if (value > 10) {
                        setGenerateForm(prev => ({ ...prev, taskCount: '10' }));
                      } else {
                        setGenerateForm(prev => ({ ...prev, taskCount: value.toString() }));
                      }
                    }}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  className="cancel-btn"
                  onClick={() => setShowGenerateModal(false)}
                >
                  Avbryt
                </button>
                <button 
                  className="generate-btn"
                  onClick={handleGenerateTasks}
                  disabled={generating}
                >
                  {generating ? 'Genererer...' : 'Generer'}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button 
            type="button"
            className="cancel-btn"
            onClick={() => navigate('/dashboard')}
          >
            Avbryt
          </button>
          <button 
            type="submit"
            className="save-btn"
            disabled={saving}
          >
            {saving ? 'Lagrer...' : 'Lagre endringer'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProgrammingLessonEditor;
